import * as React from 'react';
import type { Editor } from '@tiptap/react';

import { useTableControls } from './use-table-controls';

import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { Icon } from '@cotera/client/app/components';
import type { IconName } from '../../../icon/mapping';

export interface TableControlsButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'type'> {
  editor?: Editor | null;
}

interface TableAction {
  label: string;
  icon: IconName;
  run: (editor: Editor) => void;
}

const ACTION_SECTIONS: TableAction[][] = [
  [
    {
      label: 'Insert row above',
      icon: 'plus',
      run: (editor) => editor.chain().focus().addRowBefore().run(),
    },
    {
      label: 'Insert row below',
      icon: 'plus',
      run: (editor) => editor.chain().focus().addRowAfter().run(),
    },
    {
      label: 'Delete row',
      icon: 'x-mark',
      run: (editor) => editor.chain().focus().deleteRow().run(),
    },
  ],
  [
    {
      label: 'Insert column before',
      icon: 'plus',
      run: (editor) => editor.chain().focus().addColumnBefore().run(),
    },
    {
      label: 'Insert column after',
      icon: 'plus',
      run: (editor) => editor.chain().focus().addColumnAfter().run(),
    },
    {
      label: 'Delete column',
      icon: 'x-mark',
      run: (editor) => editor.chain().focus().deleteColumn().run(),
    },
  ],
  [
    {
      label: 'Toggle header row',
      icon: 'view-columns',
      run: (editor) => editor.chain().focus().toggleHeaderRow().run(),
    },
    {
      label: 'Toggle header column',
      icon: 'view-columns',
      run: (editor) => editor.chain().focus().toggleHeaderColumn().run(),
    },
  ],
  [
    {
      label: 'Delete table',
      icon: 'trash',
      run: (editor) => editor.chain().focus().deleteTable().run(),
    },
  ],
];

export function TableControlsButton({
  editor: providedEditor,
  ...buttonProps
}: TableControlsButtonProps) {
  const { editor, isVisible } = useTableControls({ editor: providedEditor });
  const [isOpen, setIsOpen] = React.useState(false);

  if (!isVisible || !editor) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          role="button"
          tabIndex={-1}
          aria-label="Table controls"
          title="Table"
          {...buttonProps}
        >
          <Icon icon="table" className="tiptap-button-icon" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        aria-label="Table controls"
        side="bottom"
        align="end"
        sideOffset={4}
        className="!px-0 w-[220px]"
      >
        <div className="p-1">
          {ACTION_SECTIONS.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {sectionIndex > 0 && <div className="my-1 h-px bg-border" />}
              {section.map((action) => (
                <Button
                  key={action.label}
                  variant="ghost"
                  className="w-full justify-start h-8 px-2 text-sm"
                  onClick={() => {
                    action.run(editor);
                    setIsOpen(false);
                  }}
                >
                  <Icon
                    icon={action.icon}
                    className="w-4 h-4 mr-2 text-gray-500"
                  />
                  <span>{action.label}</span>
                </Button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
